/**
 * Centralized API route registry.
 * Keep all frontend endpoint strings in one place to reduce route drift.
 */

export const apiRoutes = {
  auth: {
    csrf: "/auth/csrf",
    signin: "/auth/signin",
    signout: "/auth/signout",
    refresh: "/auth/refresh",
    register: "/auth/register",
    googleCallback: "/auth/google/callback",
    googleConnect: "/auth/google/connect",
    googleDisconnectStart: "/auth/google/disconnect/start",
    googleDisconnectConfirm: "/auth/google/disconnect/confirm",
    facebookCallback: "/auth/facebook/callback",
    passwordChange: "/auth/password/change",
    passwordReset: "/auth/password/reset",
    passwordForgot: "/auth/password/forgot",
    signinOtp: "/auth/signin/otp",
    passcode: "/auth/passcode",
    emailVerify: "/auth/email/verify",
    sessions: "/auth/sessions",
    sessionById: (sessionId: number | string): string => `/auth/sessions/${sessionId}`,
  },
  appSettings: {
    root: "/api/app-settings",
    releaseNotes: "/api/app-settings/release-notes",
    version: "/api/app-settings/version",
    experimentalFeatures: "/api/app-settings/experimental-features",
    byId: (id: number | string): string => `/api/app-settings/${id}`,
  },
  search: {
    root: "/api/search",
    suggestions: "/api/search/suggestions",
    trending: "/api/search/trending",
  },
  drafts: {
    root: "/api/drafts",
    count: "/api/drafts/count",
    byEntity: (entityType: string, id: number | string): string =>
      `/api/drafts/${encodeURIComponent(entityType)}/${encodeURIComponent(String(id))}`,
  },
  posts: {
    root: "/api/posts",
    byId: (id: number | string): string => `/api/posts/${id}`,
    /** Unified list URL; derive teabate counts client-side from returned rows (or future total metadata). */
    listByKindAndIssue: (kind: string, issueId: number | string): string =>
      `/api/posts?kind=${encodeURIComponent(kind)}&issueId=${encodeURIComponent(String(issueId))}`,
    participants: {
      list: (postId: number | string): string => `/api/posts/${postId}/participants`,
      byUser: (postId: number | string, userId: number | string): string =>
        `/api/posts/${postId}/participants/${userId}`,
      status: (postId: number | string): string => `/api/posts/${postId}/participants/status`,
    },
    /**
     * Context-comment attachments: principle comments attached to a
     * solution post, and task comments attached to an action post, both
     * managed through `entity_rel` links to owner-anchored comments.
     * See `docs/api/route-map.md` for the full contract.
     */
    principles: {
      list: (postId: number | string): string => `/api/posts/${postId}/principles`,
      create: (postId: number | string): string => `/api/posts/${postId}/principles`,
      reorder: (postId: number | string): string => `/api/posts/${postId}/principles/order`,
      unlink: (postId: number | string, commentId: number | string): string =>
        `/api/posts/${postId}/principles/${commentId}`,
    },
    tasks: {
      list: (postId: number | string): string => `/api/posts/${postId}/tasks`,
      create: (postId: number | string): string => `/api/posts/${postId}/tasks`,
      reorder: (postId: number | string): string => `/api/posts/${postId}/tasks/order`,
      unlink: (postId: number | string, commentId: number | string): string =>
        `/api/posts/${postId}/tasks/${commentId}`,
    },
  },
  issues: {
    root: "/api/issues",
    byId: (id: number | string): string => `/api/issues/id/${id}`,
    byName: (cname: string): string => `/api/issues/name/${cname}`,
    influences: (id: number | string): string => `/api/issues/id/${id}/influences`,
    image: (id: number | string): string => `/api/issues/image/${id}`,
  },
  tasks: {
    mine: "/api/tasks/mine",
    assign: (commentId: number | string): string => `/api/tasks/comments/${commentId}/assignments`,
    unassign: (commentId: number | string, userId: number | string): string =>
      `/api/tasks/comments/${commentId}/assignments/${userId}`,
    completion: (commentId: number | string): string => `/api/tasks/comments/${commentId}/completion`,
  },
  comments: {
    root: "/api/comments",
    /** Narrow, owner-aware context-comment search; powers the ContextCommentPicker. */
    search: "/api/comments/search",
    profilePrinciple: (commentId: number | string): string => `/api/comments/profile-principles/${commentId}`,
    byCommentable: (type: string, id: number | string): string => `/api/comments/${type}/${id}`,
    countByCommentable: (type: string, id: number | string): string => `/api/comments/${type}/${id}/count`,
    byId: (id: number | string): string => `/api/comments/${id}`,
    replies: (id: number | string): string => `/api/comments/${id}/replies`,
    repliesCountByCommentId: (commentId: number | string): string => `/api/comments/comment/${commentId}/replies/count`,
    crosspost: "/api/comments/crosspost",
    resteep: "/api/comments/resteep",
    teabateArguments: (teabateId: number | string): string => `/api/comments/teabate/${teabateId}/arguments`,
    teabateArgument: (teabateId: number | string): string => `/api/comments/teabate/${teabateId}/argument`,
    issueThread: (issueId: number | string): string => `/api/comments/issue/${issueId}`,
  },
  votes: {
    byEntity: (entityType: string, entityId: number | string): string => `/api/votes/${entityType}/${entityId}`,
    userByEntity: (entityType: string, entityId: number | string): string => `/api/votes/user/${entityType}/${entityId}`,
    comment: (commentId: number | string): string => `/api/votes/comment/${commentId}`,
    teabate: (teabateId: number | string): string => `/api/votes/teabate/${teabateId}`,
  },
  coauthors: {
    byEntity: (entityType: string, entityId: number | string): string => `/api/coauthors/${entityType}/${entityId}`,
    request: (entityType: string, entityId: number | string): string => `/api/coauthors/${entityType}/${entityId}/request`,
    status: (entityType: string, entityId: number | string): string => `/api/coauthors/${entityType}/${entityId}/status`,
  },
  requests: {
    root: "/api/requests",
    byId: (id: number | string): string => `/api/requests/${id}`,
    cancel: (id: number | string): string => `/api/requests/${id}/cancel`,
  },
  entities: {
    root: "/api/entities",
    media: (entityType: string, entityId: number | string): string => `/api/entities/${entityType}/${entityId}/media`,
    mediaPresign: (entityType: string, entityId: number | string): string => `/api/entities/${entityType}/${entityId}/media/presign`,
    mediaKey: (entityType: string, entityId: number | string, key: string): string => `/api/entities/${entityType}/${entityId}/media/${key}`,
    mediaReorder: (entityType: string, entityId: number | string): string => `/api/entities/${entityType}/${entityId}/media/reorder`,
    sentimentAverage: "/api/entities/sentiment/average",
    relations: "/api/entities/relations",
    relationsCounts: "/api/entities/relations/counts",
    placements: "/api/entities/placements",
    placementById: (relationId: string): string => `/api/entities/placements/${relationId}`,
    placementsActiveUsers: "/api/entities/placements/users-active",
    placementView: (relationId: string): string =>
      `/api/entities/placements/${relationId}/view`,
    placementViews: (relationId: string): string =>
      `/api/entities/placements/${relationId}/views`,
    privacy: (entityType: string, entityId: number | string): string => `/api/entities/${entityType}/${entityId}/privacy`,
  },
  user: {
    root: "/api/user",
    byId: (id: number | string): string => `/api/user/id/${id}`,
    byUsername: (username: string): string => `/api/user/${username}`,
    search: "/api/user/search",
    avatar: "/api/user/avatar",
    private: "/api/user/private",
    privacy: "/api/user/privacy",
    allowContentPromotion: "/api/user/allow-content-promotion",
    adBreakPreference: "/api/user/ad-break-preference",
    adBreakConsentPreference: "/api/user/ad-break-consent-preference",
    preferences: "/api/user/preferences",
    about: "/api/user/about",
    displayname: "/api/user/displayname",
    username: "/api/user/username",
    deactivate: "/api/user/deactivate",
    delete: "/api/user/delete",
    subscriptions: "/api/user/subscriptions",
    subscriptionsByUserId: (userId: number | string): string => `/api/user/${userId}/subscriptions`,
    subscriptionStatus: (entityType: string, entityId: number | string): string => `/api/user/subscriptions/${entityType}/${entityId}`,
    alignments: "/api/user/alignments",
    alignmentByTeabate: (teabateId: number | string): string => `/api/user/alignments/${teabateId}`,
    alignmentById: (alignmentId: number | string): string => `/api/user/alignments/${alignmentId}`,
    entitySentimentRoot: "/api/user/entity-sentiment",
    userAlignments: (userId: number | string): string => `/api/user/${userId}/alignments`,
    entitySentiment: (entityType: string, entityId: number | string): string => `/api/user/entity-sentiment/${entityType}/${entityId}`,
    relations: "/api/user/relations",
    issueRankingsReorder: "/api/user/issue-rankings/reorder",
    notificationPreferences: "/api/user/notifications/preferences",
    pushSubscription: "/api/user/push-subscription",
  },
  team: {
    root: "/api/team",
    moderated: "/api/team/moderated",
    member: "/api/team/member",
    byId: (id: number | string): string => `/api/team/${id}`,
    byName: (teamname: string): string => `/api/team/${teamname}`,
    avatar: (id: number | string): string => `/api/team/${id}/avatar`,
    members: (id: number | string): string => `/api/team/${id}/members`,
    memberRole: (id: number | string): string => `/api/team/${id}/members/role`,
    memberCount: (id: number | string): string => `/api/team/${id}/members/count`,
    private: (id: number | string): string => `/api/team/${id}/private`,
    privacy: (id: number | string): string => `/api/team/${id}/privacy`,
    allowContentPromotion: (id: number | string): string => `/api/team/${id}/allow-content-promotion`,
    preferences: (id: number | string): string => `/api/team/${id}/preferences`,
    about: (id: number | string): string => `/api/team/${id}/about`,
    teamname: (id: number | string): string => `/api/team/${id}/teamname`,
    displayname: (id: number | string): string => `/api/team/${id}/displayname`,
    invite: (id: number | string): string => `/api/team/${id}/invite`,
    status: (id: number | string): string => `/api/team/${id}/status`,
    join: (id: number | string): string => `/api/team/${id}/join`,
  },
  follow: {
    root: "/api/follow",
    status: (followerType: string, followerId: number | string, followingType: string, followingId: number | string): string =>
      `/api/follow/${followerType}/${followerId}/${followingType}/${followingId}/status`,
    bestea: (entityId: number | string, status: string): string => `/api/follow/bestea/${entityId}/${status}`,
    followers: (entityType: string, entityId: number | string): string => `/api/follow/${entityType}/${entityId}/followers`,
    following: (entityType: string, entityId: number | string): string => `/api/follow/${entityType}/${entityId}/following`,
    followersCount: (entityType: string, entityId: number | string): string => `/api/follow/${entityType}/${entityId}/followers/count`,
    followingCount: (entityType: string, entityId: number | string): string => `/api/follow/${entityType}/${entityId}/following/count`,
  },
  alignment: {
    statements: "/api/alignment/statements",
  },
  notifications: {
    root: "/api/notifications",
    byId: (id: number | string): string => `/api/notifications/${id}`,
    unreadCount: "/api/notifications/unread/count",
    markRead: "/api/notifications/read",
    markAllRead: "/api/notifications/read/all",
  },
  graph: {
    roots: "/api/graph/roots",
    collectionsFeed: "/api/graph/collections/feed",
    collectionsSearch: "/api/graph/collections/search",
    collectionsRebrew: "/api/graph/collections/rebrew",
    collectionSnapshot: (collectionId: number | string): string =>
      `/api/graph/collections/${collectionId}`,
    collectionInfluences: (collectionId: number | string): string =>
      `/api/graph/collections/${collectionId}/influences`,
    collectionInfluenceItem: (collectionId: number | string, compoundIssueId: number | string): string =>
      `/api/graph/collections/${collectionId}/items/${compoundIssueId}`,
    collectionInfluenceRestore: (collectionId: number | string, compoundIssueId: number | string): string =>
      `/api/graph/collections/${collectionId}/items/${compoundIssueId}/restore`,
    collectionIssueItem: (collectionId: number | string, issueId: number | string): string =>
      `/api/graph/collections/${collectionId}/issues/${issueId}`,
    collectionIssueRestore: (collectionId: number | string, issueId: number | string): string =>
      `/api/graph/collections/${collectionId}/issues/${issueId}/restore`,
    collectionEdgeNotes: (collectionId: number | string, compoundIssueId: number | string): string =>
      `/api/graph/collections/${collectionId}/edges/${compoundIssueId}/notes`,
  },
  community: {
    overview: "/api/community/overview",
    pipeline: "/api/community/pipeline",
    activityTrend: "/api/community/activity-trend",
    governance: "/api/community/governance",
    issueActivity: "/api/community/issue-activity",
    leaderboard: "/api/community/leaderboard",
    alignment: "/api/community/alignment",
    issueHealth: "/api/community/issue-health",
    demographics: "/api/community/demographics",
  },
  utilities: {
    urlMetadata: "/api/utilities/url/metadata",
    imageProxy: "/api/utilities/image/proxy",
    wikipediaSummary: "/api/utilities/wikipedia/summary",
    wikipediaParse: "/api/utilities/wikipedia/parse",
    wikipediaImageProxy: "/api/utilities/wikipedia/image-proxy",
    ogImage: "/api/utilities/og/image",
  },
  media: {
    sign: "/api/media/sign",
  },
  mediaAssets: {
    root: "/api/media-assets",
    byId: (id: number | string): string => `/api/media-assets/${id}`,
    presign: "/api/media-assets/presign",
    finalize: "/api/media-assets/finalize",
  },
  urls: {
    root: "/api/urls",
    byId: (id: number | string): string => `/api/urls/${id}`,
    resolve: "/api/urls/resolve",
  },
  places: {
    root: "/api/places",
    byId: (id: number | string): string => `/api/places/${id}`,
    search: "/api/places/search",
  },
  awards: {
    root: "/api/awards",
    budget: "/api/awards/budget",
    balance: "/api/awards/balance",
    transactions: "/api/awards/transactions",
    entity: (entityType: string, entityId: number | string): string => `/api/awards/entity/${entityType}/${entityId}`,
    reputation: (actorType: string, actorId: number | string): string => `/api/awards/reputation/${actorType}/${actorId}`,
    giveContent: (entityType: string, entityId: number | string): string => `/api/awards/content/${entityType}/${entityId}`,
  },
  promotionCredits: {
    root: "/api/promotion-credits",
    balance: "/api/promotion-credits/balance",
    transactions: "/api/promotion-credits/transactions",
    completeAdBreak: "/api/promotion-credits/ad-breaks/complete",
    feedAdBreak: "/api/promotion-credits/ad-breaks/feed",
  },
  promotions: {
    root: "/api/promotions",
    campaigns: "/api/promotions/campaigns",
    mine: "/api/promotions/campaigns/me",
    forTarget: (entityType: string, entityId: number | string): string =>
      `/api/promotions/campaigns/${entityType}/${entityId}`,
    cancel: (campaignId: number | string): string =>
      `/api/promotions/campaigns/${campaignId}/cancel`,
  },
  ai: {
    root: "/api/ai",
    teabateSuggestions: "/api/ai/teabates/suggestions",
    evaluateArgument: "/api/ai/arguments/evaluate",
    feedback: "/api/ai/feedback",
    graph: {
      analyze: "/api/ai/graph/analyze",
      acceptSuggestion: "/api/ai/graph/suggestions/accept",
    },
  },
  actors: {
    root: "/api/actors",
  },
  tags: {
    search: "/api/tags/search",
  },
  reports: {
    root: "/api/reports",
    byId: (id: number | string): string => `/api/reports/${id}`,
  },
  saves: {
    root: "/api/saves",
    byEntity: (entityType: string, entityId: number | string): string => `/api/saves/${entityType}/${entityId}`,
    statusByEntity: (entityType: string, entityId: number | string): string => `/api/saves/status/${entityType}/${entityId}`,
  },
  collections: {
    root: "/api/collections",
    items: (collectionId: number | string): string => `/api/collections/${collectionId}/items`,
    item: (collectionId: number | string, entityType: string, entityId: number | string): string =>
      `/api/collections/${collectionId}/items/${entityType}/${entityId}`,
    itemPosition: (collectionId: number | string, entityType: string, entityId: number | string): string =>
      `/api/collections/${collectionId}/items/${entityType}/${entityId}/position`,
  },
  polls: {
    responses: (pollId: number | string): string => `/api/polls/${pollId}/responses`,
    comment: (commentId: number | string): string => `/api/polls/comment/${commentId}`,
    message: (messageId: number | string): string => `/api/polls/message/${messageId}`,
  },
  chat: {
    root: "/api/chat",
    share: "/api/chat/share",
    conversations: "/api/chat/conversations",
    conversation: (publicId: string): string => `/api/chat/conversations/${publicId}`,
    messages: (publicId: string): string => `/api/chat/conversations/${publicId}/messages`,
    mediaPresign: (publicId: string): string => `/api/chat/conversations/${publicId}/media/presign`,
    media: (publicId: string): string => `/api/chat/conversations/${publicId}/media`,
    read: (publicId: string): string => `/api/chat/conversations/${publicId}/read`,
    message: (messagePublicId: string): string => `/api/chat/messages/${messagePublicId}`,
    thread: (messagePublicId: string): string => `/api/chat/messages/${messagePublicId}/thread`,
    reactions: (messagePublicId: string): string => `/api/chat/messages/${messagePublicId}/reactions`,
    reaction: (messagePublicId: string, reactionKey: string): string => `/api/chat/messages/${messagePublicId}/reactions/${encodeURIComponent(reactionKey)}`,
    requests: "/api/chat/requests",
    requestAccept: (requestId: number | string): string => `/api/chat/requests/${requestId}/accept`,
    requestDecline: (requestId: number | string): string => `/api/chat/requests/${requestId}/decline`,
    requestBlock: (requestId: number | string): string => `/api/chat/requests/${requestId}/block`,
    unreadCount: "/api/chat/unread/count",
    settings: "/api/chat/settings",
  },
  quiz: {
    root: "/api/quiz",
    byCode: (code: string): string => `/api/quiz/${encodeURIComponent(code)}`,
    join: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/join`,
    qr: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/qr`,
    invites: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/invites`,
    start: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/start`,
    issues: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/issues`,
    teabate: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/teabate`,
    startPart2: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/start-part2`,
    guesses: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/guesses`,
    responses: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/responses`,
    end: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/end`,
    results: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/results`,
    publish: (code: string): string => `/api/quiz/${encodeURIComponent(code)}/publish`,
  },
} as const;

const mountedPrefixes = new Set<string>([
  "/auth",
  "/api/issues",
  "/api/user",
  "/api/comments",
  "/api/votes",
  "/api/entities",
  "/api/saves",
  "/api/collections",
  "/api/reports",
  "/api/tags",
  "/api/mentions",
  "/api/utilities",
  "/api/team",
  "/api/coauthors",
  "/api/requests",
  "/api/follow",
  "/api/notifications",
  "/api/actors",
  "/api/posts",
  "/api/app-settings",
  "/api/search",
  "/api/polls",
  "/api/graph",
  "/api/community",
  "/api/media",
  "/api/media-assets",
  "/api/urls",
  "/api/places",
  "/api/awards",
  "/api/promotion-credits",
  "/api/ai",
  "/api/chat",
  "/api/quiz",
]);

const graphPathAliases = new Map<string, string>([
  ["/api/graph/path", apiRoutes.graph.roots],
]);

const isAbsoluteHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url);

const getRoutePrefix = (pathname: string): string => {
  if (pathname.startsWith("/auth")) {
    return "/auth";
  }
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length >= 2) {
    return `/${segments[0]}/${segments[1]}`;
  }
  return pathname;
};

/**
 * Normalize a URL path through the route registry so callers can use a single
 * coverage point even when some modules still pass inline strings.
 */
export const normalizeApiRoutePath = (pathOrUrl?: string): string | undefined => {
  if (!pathOrUrl || typeof pathOrUrl !== "string") {
    return pathOrUrl;
  }

  const trimmed = pathOrUrl.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (!trimmed.startsWith("/") && !isAbsoluteHttpUrl(trimmed)) {
    return trimmed;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "https://local.invalid";
  const parsed = new URL(trimmed, origin);
  const normalizedPath = graphPathAliases.get(parsed.pathname) || parsed.pathname;

  const prefix = getRoutePrefix(normalizedPath);
  if (process.env.NODE_ENV !== "production") {
    if (
      (normalizedPath.startsWith("/api/") || normalizedPath.startsWith("/auth/"))
      && !mountedPrefixes.has(prefix)
    ) {
      // eslint-disable-next-line no-console
      console.warn(`[apiRoutes] Route prefix is not mounted in backend server: ${normalizedPath}`);
    }
  }

  if (isAbsoluteHttpUrl(trimmed)) {
    parsed.pathname = normalizedPath;
    return parsed.toString();
  }

  return `${normalizedPath}${parsed.search || ""}${parsed.hash || ""}`;
};

export const buildApiAbsoluteUrl = (
  pathOrUrl: string,
  params?: Record<string, string | number | boolean | undefined | null>
): string => {
  const normalized = normalizeApiRoutePath(pathOrUrl) || pathOrUrl;
  const base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/$/, "");

  const absolute = isAbsoluteHttpUrl(normalized)
    ? new URL(normalized)
    : new URL(`${base}${normalized.startsWith("/") ? normalized : `/${normalized}`}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      absolute.searchParams.set(key, String(value));
    });
  }

  return absolute.toString();
};
